'use client';

import { PropsWithChildren, useState } from 'react';
import { addDays, format } from 'date-fns';
import { Bot, CalendarPlus, Info, PhoneCall, PieChartIcon } from 'lucide-react';
import { DateRange as ReactDateRange } from 'react-day-picker';

import { PageMain } from '@/app/(app)/page-main';
import DateRange from '@/components/daterange';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OFFICE_IDS } from '@/consts/offices';
import { cn } from '@/lib/utils';

import AnimateValue from './components/animate-value';
import PieChart from './components/pie-chart';

// 🎑 View
export function HomePage() {
  const [fromDate, setFromDate] = useState(() => {
    const date = addDays(new Date(), -7);
    return { date, formatted: format(date, 'yyyy-MM-dd') };
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return { date, formatted: format(date, 'yyyy-MM-dd') };
  });
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { data, isLoading } = api.calls.getCallAnalytics.useQuery(
    {
      query: {
        office_id: officeId,
        from_date: fromDate.formatted,
        to_date: toDate.formatted,
      },
    },
    {
      placeholderData: (prev) => prev,
    }
  );

  const handleDateChange = (range: ReactDateRange | undefined) => {
    if (!range) return;
    if (!range.from || !range.to) return;

    const fromFormatted = format(range.from, 'yyyy-MM-dd');
    const toFormatted = format(range.to, 'yyyy-MM-dd');

    if (fromFormatted !== fromDate.formatted) {
      setFromDate({
        date: range.from,
        formatted: fromFormatted,
      });
    }
    if (toFormatted !== toDate.formatted) {
      setToDate({
        date: range.to,
        formatted: toFormatted,
      });
    }
  };

  if (!data) {
    if (isLoading) {
      return (
        <PageMain title='Analytics' right={<DateRange value={{ from: fromDate.date, to: toDate.date }} disabled />}>
          <Skeleton className='min-h-[80dvh] w-full' />
        </PageMain>
      );
    }
    return (
      <div className='flex h-full flex-col items-center justify-center'>
        <div className='flex flex-col gap-2 rounded-2xl bg-background p-8 shadow-md'>
          <PieChartIcon className='size-9' />
          <h2 className='mt-2 text-heading-lg font-semibold'>Analytics</h2>
          <div className='text-sm text-muted-foreground'>
            You don&apos;t have any analytics yet, please check back later.
          </div>
        </div>
      </div>
    );
  }

  const { calls, appointments, engagement } = formatData(data);

  return (
    <PageMain
      title='Analytics'
      right={<DateRange value={{ from: fromDate.date, to: toDate.date }} onCommit={handleDateChange} />}
    >
      <div className={cn('space-y-6', isLoading && 'animate-pulse')}>
        <Section icon={PhoneCall} title='Calls' columns={2}>
          <div className='flex flex-col space-y-4'>
            {calls.map((call) => {
              return <CustomCard key={call.label} {...call} />;
            })}
          </div>

          <OfficeVsAfterHoursChart
            label='When did customers call?'
            officeHours={data.calls_during_office_hours}
            afterHours={data.calls_outside_office_hours}
          />
        </Section>

        <Section icon={CalendarPlus} title='Appointments'>
          {appointments.map((appointment) => {
            return <CustomCard key={appointment.label} {...appointment} />;
          })}
        </Section>

        <Section icon={Bot} title='Engagement' columns={2}>
          {engagement.map((engagement) => {
            return <CustomCard key={engagement.label} {...engagement} />;
          })}

          {/* <CustomCard label='Call reason' title='TBD' /> */}
        </Section>
      </div>
    </PageMain>
  );
}

// 🍰 Components
type SectionProps = PropsWithChildren & {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  columns?: 2 | 3 | 4;
};

function Section({ icon: Icon, title, children, columns = 3 }: SectionProps) {
  return (
    <div className='flex flex-col gap-2 rounded-xl border bg-background p-3 shadow-xs'>
      <div className='flex items-center space-x-2.5 p-2'>
        {Icon && <Icon className='size-5 text-muted-foreground' />}
        <h2 className='text-heading-lg font-semibold'>{title}</h2>
      </div>

      <div
        className={cn('grid gap-4', {
          'lg:grid-cols-2': columns === 2,
          'lg:grid-cols-3': columns === 3,
          'grid-cols-4': columns === 4,
        })}
      >
        {children}
      </div>
    </div>
  );
}

type CustomCardProps = PropsWithChildren & {
  label: string;
  tooltip?: string;
  title?: string | number;
  format?: 'number' | 'currency' | 'text' | 'percentage';
  direction?: 'horizontal' | 'vertical';
};

function CustomCard({ label, tooltip, title, children, format = 'text', direction = 'vertical' }: CustomCardProps) {
  const formattedTitle = (() => {
    switch (format) {
      case 'number':
        return <AnimateValue style='decimal' value={Number(title)} />;
      case 'currency':
        return <AnimateValue style='currency' value={Number(title)} />;
      case 'percentage':
        return <AnimateValue style='percent' value={Number(title)} />;
      default:
        return title;
    }
  })();

  return (
    <Card className='grow shadow-none'>
      <CardHeader
        className={cn('flex flex-col items-start', {
          'h-12 flex-row items-center justify-between': direction === 'horizontal',
        })}
      >
        <CardDescription className='flex items-center space-x-1'>
          <div>{label}</div>

          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className='size-3.5' />
                </TooltipTrigger>

                <TooltipContent className='max-w-64 text-center text-balance'>{tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardDescription>

        <CardTitle className='text-3xl font-bold'>{formattedTitle}</CardTitle>
      </CardHeader>

      {children && <div>{children}</div>}
    </Card>
  );
}

type OfficeVsAfterHoursChartProps = CustomCardProps & {
  officeHours: number;
  afterHours: number;
};

function OfficeVsAfterHoursChart({ officeHours, afterHours, ...customCardProps }: OfficeVsAfterHoursChartProps) {
  const data = [
    { type: 'office', hours: officeHours, fill: 'var(--color-office)' },
    { type: 'after', hours: afterHours, fill: 'var(--color-after)' },
  ];

  const config = {
    office: {
      label: 'Office hours',
      color: 'var(--chart-1)',
    },
    after: {
      label: 'After hours',
      color: 'var(--chart-2)',
    },
  } satisfies ChartConfig;

  return (
    <CustomCard {...customCardProps}>
      <div className='flex items-center px-6'>
        <div className='hidden flex-1 space-y-1 lg:block'>
          {data.map((item) => {
            const { type, hours } = item;
            const { label, color } = config[type as keyof typeof config];

            return (
              <div key={type} className='flex items-center space-x-2'>
                <div className='size-4 rounded-sm' style={{ backgroundColor: color }} />
                <p className='text-sm text-muted-foreground'>
                  {label}: {hours}
                </p>
              </div>
            );
          })}
        </div>

        <div className='flex-1'>
          <PieChart config={config} data={data} dataKey='hours' nameKey='type' />
        </div>
      </div>
    </CustomCard>
  );
}

// 🛠️ Utils
function formatData(data: ApiSchemas['CallAnalytics']) {
  // 📞 Calls
  const calls: CustomCardProps[] = [
    {
      label: 'Calls',
      title: data.total_calls,
      format: 'number',
    },
    {
      label: 'Minutes',
      title: data.total_minutes,
      format: 'number',
    },
  ];

  // 📅 Appointments
  const appointments: CustomCardProps[] = [
    {
      label: 'Appointments booked',
      tooltip: 'Number of appointments successfully booked',
      title: data.appointments_booked,
      format: 'number',
      direction: 'horizontal',
    },
    {
      label: 'Revenue generated',
      tooltip:
        'Calculated by multiplying each booked appointment by the price of the service booked. This is an estimate and accuracy depends on the data available in your system',
      title: 'N/A',
      format: 'text',
      direction: 'horizontal',
    },
    {
      label: 'Conversion rate',
      tooltip: 'Percentage of callers who called to book an appointment and successfully did',
      title: Math.min(Math.round((data.booked_success / data.intent_to_book) * 100) || 0, 100),
      format: 'percentage',
      direction: 'horizontal',
    },
    {
      label: 'Appointments rescheduled',
      tooltip: 'Number of appointments successfully rescheduled',
      title: data.appointments_rescheduled,
      format: 'number',
      direction: 'horizontal',
    },
    {
      label: 'Appointments cancelled',
      tooltip: 'Number of appointments successfully cancelled',
      title: data.appointments_cancelled,
      format: 'number',
      direction: 'horizontal',
    },
    {
      label: 'Calls transferred',
      title: data.transfer_completed,
      format: 'number',
      direction: 'horizontal',
    },
  ];

  // 🤖 Engagement
  const engagement: CustomCardProps[] = [
    {
      label: 'Spam Calls',
      tooltip: 'Calls with no interaction with the AI',
      title: data.no_engagement,
      format: 'number',
      direction: 'horizontal',
    },
  ];

  const OFFICE_IDS_TO_HIDE_OUTCOME = [OFFICE_IDS.HORIZON, OFFICE_IDS.APOLLOX];

  if (!OFFICE_IDS_TO_HIDE_OUTCOME.includes(data.office_id)) {
    engagement.unshift({
      label: 'Calls where a human was requested',
      tooltip: 'Number of calls where the caller requested to be transferred to a human representative',
      title: data.human_requested,
      format: 'number',
      direction: 'horizontal',
    });
  }

  return { calls, appointments, engagement };
}
